import React, { useEffect, useMemo, useState } from 'react';
import { Alert, AlertDescription, AlertTitle } from '../../ui/alert';
import { Badge } from '../../ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { AlertCircle, PieChart } from 'lucide-react';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
// @ts-ignore
import jStat from 'jstat';
import { ParsedWorksheet, WorksheetColumn } from './worksheet';

interface TablesCategoricalAnalysisProps {
  worksheet: ParsedWorksheet;
  selectedColumnId?: string;
}

type AnalysisType = 'summary' | 'crosstab' | 'chi-square' | 'fishers-exact';

type CategoryCount = {
  label: string;
  count: number;
  proportion: number;
};

type CrossTabRow = {
  rowLabel: string;
  counts: Record<string, number>;
  rowTotal: number;
};

const formatNumber = (value: number, digits = 4) => (Number.isFinite(value) ? value.toFixed(digits) : '—');
const formatPercent = (value: number) => (Number.isFinite(value) ? `${(value * 100).toFixed(1)}%` : '—');

const normalizeLabel = (value: string | undefined | null) => {
  const trimmed = String(value ?? '').trim();
  return trimmed || '(blank)';
};

const buildCategoryCounts = (column?: WorksheetColumn) => {
  if (!column) return null;

  const counts = new Map<string, number>();
  column.values.forEach((value) => {
    const label = normalizeLabel(value);
    counts.set(label, (counts.get(label) ?? 0) + 1);
  });

  const total = column.values.length;
  if (total === 0) return null;

  const categories = Array.from(counts.entries())
    .map(([label, count]) => ({ label, count, proportion: count / total }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));

  return {
    total,
    distinct: categories.length,
    mode: categories[0]?.label ?? '—',
    categories,
    topCategories: categories.slice(0, 10),
  };
};

const buildCrossTab = (rowColumn?: WorksheetColumn, columnColumn?: WorksheetColumn) => {
  if (!rowColumn || !columnColumn) return null;

  const rowCount = Math.min(rowColumn.values.length, columnColumn.values.length);
  const pairedRows = Array.from({ length: rowCount }, (_, index) => ({
    rowLabel: normalizeLabel(rowColumn.values[index]),
    columnLabel: normalizeLabel(columnColumn.values[index]),
  }));

  if (pairedRows.length === 0) return null;

  const rowLabels = Array.from(new Set(pairedRows.map((row) => row.rowLabel))).sort((a, b) => a.localeCompare(b));
  const columnLabels = Array.from(new Set(pairedRows.map((row) => row.columnLabel))).sort((a, b) => a.localeCompare(b));
  if (rowLabels.length < 1 || columnLabels.length < 1) return null;

  const table = new Map<string, number>();
  pairedRows.forEach((row) => {
    const key = `${row.rowLabel}__${row.columnLabel}`;
    table.set(key, (table.get(key) ?? 0) + 1);
  });

  const rows: CrossTabRow[] = rowLabels.map((rowLabel) => {
    const counts = Object.fromEntries(
      columnLabels.map((columnLabel) => [columnLabel, table.get(`${rowLabel}__${columnLabel}`) ?? 0]),
    );
    const rowTotal = columnLabels.reduce((sum, columnLabel) => sum + (counts[columnLabel] ?? 0), 0);
    return { rowLabel, counts, rowTotal };
  });

  const columnTotals = Object.fromEntries(
    columnLabels.map((columnLabel) => [columnLabel, rows.reduce((sum, row) => sum + (row.counts[columnLabel] ?? 0), 0)]),
  );
  const grandTotal = rows.reduce((sum, row) => sum + row.rowTotal, 0);

  return {
    rowLabels,
    columnLabels,
    rows,
    columnTotals,
    grandTotal,
    chartData: rows.map((row) => ({
      rowLabel: row.rowLabel,
      ...Object.fromEntries(columnLabels.map((columnLabel) => [columnLabel, row.counts[columnLabel] ?? 0])),
    })),
  };
};

const chiSquareIndependence = (crossTab: ReturnType<typeof buildCrossTab>) => {
  if (!crossTab || crossTab.rowLabels.length < 2 || crossTab.columnLabels.length < 2 || crossTab.grandTotal <= 0) {
    return null;
  }

  let chiSquare = 0;
  let minExpected = Number.POSITIVE_INFINITY;
  let lowExpectedCells = 0;
  const expectedRows = crossTab.rows.map((row) => {
    const expectedCounts = Object.fromEntries(
      crossTab.columnLabels.map((columnLabel) => {
        const expected = (row.rowTotal * (crossTab.columnTotals[columnLabel] ?? 0)) / crossTab.grandTotal;
        minExpected = Math.min(minExpected, expected);
        if (expected < 5) lowExpectedCells += 1;
        const observed = row.counts[columnLabel] ?? 0;
        chiSquare += expected > 0 ? ((observed - expected) ** 2) / expected : 0;
        return [columnLabel, expected];
      }),
    );

    return {
      rowLabel: row.rowLabel,
      expectedCounts,
    };
  });

  const degreesOfFreedom = (crossTab.rowLabels.length - 1) * (crossTab.columnLabels.length - 1);
  const pValue = degreesOfFreedom > 0 ? 1 - jStat.chisquare.cdf(chiSquare, degreesOfFreedom) : Number.NaN;
  const cramerV = degreesOfFreedom > 0
    ? Math.sqrt(chiSquare / (crossTab.grandTotal * Math.min(crossTab.rowLabels.length - 1, crossTab.columnLabels.length - 1)))
    : Number.NaN;

  return {
    chiSquare,
    degreesOfFreedom,
    pValue,
    cramerV,
    minExpected,
    lowExpectedCells,
    expectedRows,
  };
};

const logFactorial = (n: number) => {
  let total = 0;
  for (let index = 2; index <= n; index += 1) {
    total += Math.log(index);
  }
  return total;
};

const logCombination = (n: number, k: number) => {
  if (k < 0 || k > n) return Number.NEGATIVE_INFINITY;
  return logFactorial(n) - logFactorial(k) - logFactorial(n - k);
};

const hypergeometricProbability = (x: number, row1: number, row2: number, col1: number, total: number) => {
  return Math.exp(logCombination(row1, x) + logCombination(row2, col1 - x) - logCombination(total, col1));
};

const fishersExact = (crossTab: ReturnType<typeof buildCrossTab>) => {
  if (!crossTab || crossTab.rowLabels.length !== 2 || crossTab.columnLabels.length !== 2) return null;

  const [rowA, rowB] = crossTab.rows;
  const [colA, colB] = crossTab.columnLabels;
  const a = rowA.counts[colA] ?? 0;
  const b = rowA.counts[colB] ?? 0;
  const c = rowB.counts[colA] ?? 0;
  const d = rowB.counts[colB] ?? 0;

  const row1 = a + b;
  const row2 = c + d;
  const col1 = a + c;
  const total = crossTab.grandTotal;

  const minX = Math.max(0, col1 - row2);
  const maxX = Math.min(row1, col1);
  const observedProbability = hypergeometricProbability(a, row1, row2, col1, total);

  let twoSidedP = 0;
  for (let x = minX; x <= maxX; x += 1) {
    const probability = hypergeometricProbability(x, row1, row2, col1, total);
    if (probability <= observedProbability + 1e-12) {
      twoSidedP += probability;
    }
  }

  const oddsRatio = b * c === 0 ? (a * d === 0 ? 0 : Number.POSITIVE_INFINITY) : (a * d) / (b * c);

  return {
    a,
    b,
    c,
    d,
    oddsRatio,
    pValue: Math.min(1, twoSidedP),
    rowLabels: crossTab.rowLabels,
    columnLabels: crossTab.columnLabels,
  };
};

export const TablesCategoricalAnalysis: React.FC<TablesCategoricalAnalysisProps> = ({ worksheet, selectedColumnId }) => {
  const categoricalColumns = worksheet.columns.filter((column) => column.nonEmptyCount >= 2);

  const [analysisType, setAnalysisType] = useState<AnalysisType>('summary');
  const [primaryColumnId, setPrimaryColumnId] = useState(selectedColumnId ?? categoricalColumns[0]?.id ?? '');
  const [secondaryColumnId, setSecondaryColumnId] = useState(categoricalColumns[1]?.id ?? categoricalColumns[0]?.id ?? '');

  useEffect(() => {
    if (!primaryColumnId && categoricalColumns[0]) {
      setPrimaryColumnId(selectedColumnId && categoricalColumns.some((column) => column.id === selectedColumnId)
        ? selectedColumnId
        : categoricalColumns[0].id);
    }
    if (!secondaryColumnId && categoricalColumns[1]) {
      setSecondaryColumnId(categoricalColumns[1].id);
    }
  }, [categoricalColumns, primaryColumnId, secondaryColumnId, selectedColumnId]);

  const primaryColumn = categoricalColumns.find((column) => column.id === primaryColumnId) ?? categoricalColumns[0];
  const secondaryColumn = categoricalColumns.find((column) => column.id === secondaryColumnId) ?? categoricalColumns[1] ?? categoricalColumns[0];

  const summaryResult = useMemo(() => buildCategoryCounts(primaryColumn), [primaryColumn]);
  const crossTabResult = useMemo(() => buildCrossTab(primaryColumn, secondaryColumn), [primaryColumn, secondaryColumn]);
  const chiSquareResult = useMemo(() => chiSquareIndependence(crossTabResult), [crossTabResult]);
  const fisherResult = useMemo(() => fishersExact(crossTabResult), [crossTabResult]);

  if (categoricalColumns.length === 0) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>No usable worksheet columns</AlertTitle>
        <AlertDescription>Paste worksheet data with at least one populated column to unlock tables and categorical analysis.</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <Badge>Ready</Badge>
                <Badge variant="outline">Categorical workflows</Badge>
              </div>
              <CardTitle className="mt-3 flex items-center gap-2 text-3xl">
                <PieChart className="h-8 w-8 text-sky-700" />
                Tables & Categorical Analysis
              </CardTitle>
              <CardDescription className="mt-2 max-w-3xl text-base">
                Build one-way summaries, cross tabs, chi-square independence tests, and Fisher&apos;s exact tests directly from worksheet columns.
              </CardDescription>
            </div>
            <div className="grid min-w-[15rem] grid-cols-2 gap-3 text-sm">
              <div className="rounded-xl border bg-muted/30 p-3">
                <div className="text-muted-foreground">Worksheet columns</div>
                <div className="mt-1 text-2xl font-semibold">{worksheet.columnCount}</div>
              </div>
              <div className="rounded-xl border bg-muted/30 p-3">
                <div className="text-muted-foreground">Worksheet rows</div>
                <div className="mt-1 text-2xl font-semibold">{worksheet.rowCount}</div>
              </div>
            </div>
          </div>
        </CardHeader>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Procedure setup</CardTitle>
          <CardDescription>Choose a categorical workflow and map worksheet columns into row and column roles.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          <div className="space-y-2">
            <Label>Workflow</Label>
            <Select value={analysisType} onValueChange={(value: AnalysisType) => setAnalysisType(value)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="summary">Categorical summary</SelectItem>
                <SelectItem value="crosstab">Cross tabulation</SelectItem>
                <SelectItem value="chi-square">Chi-square test of independence</SelectItem>
                <SelectItem value="fishers-exact">Fisher&apos;s exact test (2×2)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>{analysisType === 'summary' ? 'Category column' : 'Row column'}</Label>
            <Select value={primaryColumnId} onValueChange={setPrimaryColumnId}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {categoricalColumns.map((column) => (
                  <SelectItem key={column.id} value={column.id}>{column.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {analysisType !== 'summary' && (
            <div className="space-y-2">
              <Label>Column column</Label>
              <Select value={secondaryColumnId} onValueChange={setSecondaryColumnId}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categoricalColumns.map((column) => (
                    <SelectItem key={column.id} value={column.id}>{column.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </CardContent>
      </Card>

      {analysisType === 'summary' && (
        summaryResult ? (
          <>
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Observations</CardTitle></CardHeader><CardContent><div className="text-xl font-semibold">{summaryResult.total}</div></CardContent></Card>
              <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Distinct categories</CardTitle></CardHeader><CardContent><div className="text-xl font-semibold">{summaryResult.distinct}</div></CardContent></Card>
              <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Mode</CardTitle></CardHeader><CardContent><div className="text-xl font-semibold">{summaryResult.mode}</div></CardContent></Card>
              <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Coverage</CardTitle></CardHeader><CardContent><div className="text-xl font-semibold">{formatPercent(summaryResult.topCategories.reduce((sum, category) => sum + category.proportion, 0))}</div></CardContent></Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Category profile</CardTitle>
                <CardDescription>Counts and proportions for the selected categorical variable.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={summaryResult.topCategories}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="label" interval={0} angle={-18} textAnchor="end" height={72} />
                      <YAxis allowDecimals={false} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#0369a1" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Category</TableHead>
                      <TableHead className="text-right">Count</TableHead>
                      <TableHead className="text-right">Proportion</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {summaryResult.categories.map((category) => (
                      <TableRow key={category.label}>
                        <TableCell>{category.label}</TableCell>
                        <TableCell className="text-right">{category.count}</TableCell>
                        <TableCell className="text-right">{formatPercent(category.proportion)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </>
        ) : (
          <Alert><AlertCircle className="h-4 w-4" /><AlertTitle>Need a populated column</AlertTitle><AlertDescription>Select a worksheet column with data to generate a categorical summary.</AlertDescription></Alert>
        )
      )}

      {analysisType === 'crosstab' && (
        crossTabResult ? (
          <>
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Row categories</CardTitle></CardHeader><CardContent><div className="text-xl font-semibold">{crossTabResult.rowLabels.length}</div></CardContent></Card>
              <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Column categories</CardTitle></CardHeader><CardContent><div className="text-xl font-semibold">{crossTabResult.columnLabels.length}</div></CardContent></Card>
              <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Observations</CardTitle></CardHeader><CardContent><div className="text-xl font-semibold">{crossTabResult.grandTotal}</div></CardContent></Card>
              <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Cells</CardTitle></CardHeader><CardContent><div className="text-xl font-semibold">{crossTabResult.rowLabels.length * crossTabResult.columnLabels.length}</div></CardContent></Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Cross-tab profile</CardTitle>
                <CardDescription>Observed counts for each row and column combination.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={crossTabResult.chartData}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="rowLabel" />
                      <YAxis allowDecimals={false} />
                      <Tooltip />
                      <Legend />
                      {crossTabResult.columnLabels.map((columnLabel, index) => (
                        <Bar key={columnLabel} dataKey={columnLabel} stackId="counts" fill={["#0369a1", "#0f766e", "#7c3aed", "#ea580c", "#16a34a", "#dc2626"][index % 6]} radius={index === crossTabResult.columnLabels.length - 1 ? [6, 6, 0, 0] : [0, 0, 0, 0]} />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{primaryColumn?.name ?? 'Row'}</TableHead>
                      {crossTabResult.columnLabels.map((columnLabel) => (
                        <TableHead key={columnLabel} className="text-right">{columnLabel}</TableHead>
                      ))}
                      <TableHead className="text-right">Row total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {crossTabResult.rows.map((row) => (
                      <TableRow key={row.rowLabel}>
                        <TableCell>{row.rowLabel}</TableCell>
                        {crossTabResult.columnLabels.map((columnLabel) => (
                          <TableCell key={columnLabel} className="text-right">{row.counts[columnLabel] ?? 0}</TableCell>
                        ))}
                        <TableCell className="text-right font-medium">{row.rowTotal}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell className="font-medium">Column total</TableCell>
                      {crossTabResult.columnLabels.map((columnLabel) => (
                        <TableCell key={columnLabel} className="text-right font-medium">{crossTabResult.columnTotals[columnLabel] ?? 0}</TableCell>
                      ))}
                      <TableCell className="text-right font-medium">{crossTabResult.grandTotal}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </>
        ) : (
          <Alert><AlertCircle className="h-4 w-4" /><AlertTitle>Need two worksheet columns</AlertTitle><AlertDescription>Select two populated columns to build a cross tabulation.</AlertDescription></Alert>
        )
      )}

      {analysisType === 'chi-square' && (
        crossTabResult && chiSquareResult ? (
          <>
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
              <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Chi-square</CardTitle></CardHeader><CardContent><div className="text-xl font-semibold">{formatNumber(chiSquareResult.chiSquare)}</div></CardContent></Card>
              <Card><CardHeader className="pb-2"><CardTitle className="text-sm">df</CardTitle></CardHeader><CardContent><div className="text-xl font-semibold">{chiSquareResult.degreesOfFreedom}</div></CardContent></Card>
              <Card><CardHeader className="pb-2"><CardTitle className="text-sm">p-value</CardTitle></CardHeader><CardContent><div className="text-xl font-semibold">{formatNumber(chiSquareResult.pValue)}</div></CardContent></Card>
              <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Cramer's V</CardTitle></CardHeader><CardContent><div className="text-xl font-semibold">{formatNumber(chiSquareResult.cramerV)}</div></CardContent></Card>
              <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Min expected</CardTitle></CardHeader><CardContent><div className="text-xl font-semibold">{formatNumber(chiSquareResult.minExpected)}</div></CardContent></Card>
            </div>

            {chiSquareResult.lowExpectedCells > 0 ? (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Small expected counts detected</AlertTitle>
                <AlertDescription>{chiSquareResult.lowExpectedCells} cells have expected counts below 5, so the chi-square approximation may be weak.</AlertDescription>
              </Alert>
            ) : null}

            <Card>
              <CardHeader>
                <CardTitle>Observed vs expected counts</CardTitle>
                <CardDescription>Compare the observed cross tab with the expected cell counts under independence.</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{primaryColumn?.name ?? 'Row'}</TableHead>
                      {crossTabResult.columnLabels.map((columnLabel) => (
                        <TableHead key={columnLabel} className="text-right">{columnLabel}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {crossTabResult.rows.map((row, rowIndex) => (
                      <TableRow key={row.rowLabel}>
                        <TableCell>{row.rowLabel}</TableCell>
                        {crossTabResult.columnLabels.map((columnLabel) => (
                          <TableCell key={columnLabel} className="text-right">
                            <div>{row.counts[columnLabel] ?? 0}</div>
                            <div className="text-xs text-muted-foreground">E {formatNumber(chiSquareResult.expectedRows[rowIndex].expectedCounts[columnLabel] ?? Number.NaN)}</div>
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </>
        ) : (
          <Alert><AlertCircle className="h-4 w-4" /><AlertTitle>Need a two-way table</AlertTitle><AlertDescription>Select two populated columns with at least two categories each to run a chi-square test.</AlertDescription></Alert>
        )
      )}

      {analysisType === 'fishers-exact' && (
        fisherResult ? (
          <>
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Two-sided p-value</CardTitle></CardHeader><CardContent><div className="text-xl font-semibold">{formatNumber(fisherResult.pValue)}</div></CardContent></Card>
              <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Odds ratio</CardTitle></CardHeader><CardContent><div className="text-xl font-semibold">{formatNumber(fisherResult.oddsRatio)}</div></CardContent></Card>
              <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Row levels</CardTitle></CardHeader><CardContent><div className="text-xl font-semibold">2</div></CardContent></Card>
              <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Column levels</CardTitle></CardHeader><CardContent><div className="text-xl font-semibold">2</div></CardContent></Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>2×2 contingency table</CardTitle>
                <CardDescription>Fisher&apos;s exact test is computed from the observed 2×2 table with fixed margins.</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead></TableHead>
                      <TableHead className="text-right">{fisherResult.columnLabels[0]}</TableHead>
                      <TableHead className="text-right">{fisherResult.columnLabels[1]}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell>{fisherResult.rowLabels[0]}</TableCell>
                      <TableCell className="text-right">{fisherResult.a}</TableCell>
                      <TableCell className="text-right">{fisherResult.b}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>{fisherResult.rowLabels[1]}</TableCell>
                      <TableCell className="text-right">{fisherResult.c}</TableCell>
                      <TableCell className="text-right">{fisherResult.d}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </>
        ) : (
          <Alert><AlertCircle className="h-4 w-4" /><AlertTitle>Fisher&apos;s exact needs a 2×2 table</AlertTitle><AlertDescription>Select two worksheet columns that each collapse to exactly two categories after blanks are grouped.</AlertDescription></Alert>
        )
      )}
    </div>
  );
};
